/**
 * Answering a tool call ADK paused on: a confirmation or a choice, and which
 * one's reply is in flight. Split out of `useAgentChat.ts` with no behavior
 * change.
 */
import { useCallback, useState } from 'react';

import {
  clearPendingChoice,
  clearPendingConfirmation,
  respondToPendingChoice,
  respondToPendingConfirmation,
} from './agentChatReducer';
import type { ChatCore } from './useAgentChatCore';
import type { SendChatTurn } from './useAgentChatTurn';
import type { UseAgentChatReturn } from './useAgentChat.types';

export type ChatHitlReplies = Pick<
  UseAgentChatReturn,
  'respondToConfirmation' | 'respondingConfirmationId' | 'respondToChoice' | 'respondingChoiceId'
>;

export function useAgentChatHitlReplies(
  chat: ChatCore,
  sendMessage: SendChatTurn,
): ChatHitlReplies {
  // Which confirmation's reply is in flight — locks that dialog's buttons so a
  // second click cannot send a duplicate approval for a destructive action.
  const [respondingConfirmationId, setRespondingConfirmationId] = useState<string | null>(null);
  /** Which question's answer is in flight — locks that control's options. */
  const [respondingChoiceId, setRespondingChoiceId] = useState<string | null>(null);

  /**
   * Answer one paused tool call.
   *
   * The reply is a FunctionResponse, not a message, so nothing is added to the
   * transcript on the way out. The dialog is cleared only AFTER the reply is
   * away — see `respondToPendingConfirmation`, which owns that order.
   *
   * Returns whether the reply was SENT. `false` means another turn was still
   * streaming and the dialog is still up; the caller must tell the researcher,
   * because they pressed a button on a destructive action. The toast lives at
   * the call site rather than here: `useToast` reaches Chakra's breakpoint
   * hook, and pulling that into this hook forces a ChakraProvider on every
   * test that renders it.
   */
  const respondToConfirmation = useCallback(
    async (callId: string, confirmed: boolean): Promise<boolean> => {
      // Order (send, THEN clear) is load-bearing and lives in the helper so it
      // is testable — see respondToPendingConfirmation.
      setRespondingConfirmationId(callId);
      try {
        return await respondToPendingConfirmation(callId, confirmed, {
          // The text is a placeholder the server discards; it exists only
          // because the multipart endpoint requires a non-empty `message`.
          send: (reply) => sendMessage('.', undefined, undefined, { toolConfirmation: reply }),
          clear: (id) => chat.setMessages((prev) => clearPendingConfirmation(prev, id)),
        });
      } finally {
        setRespondingConfirmationId(null);
      }
    },
    [chat, sendMessage],
  );

  /**
   * Answer one paused question. Mirrors `respondToConfirmation`, including the
   * send-then-clear order that keeps a failed reply from clearing the control.
   */
  const respondToChoice = useCallback(
    async (callId: string, value: string): Promise<boolean> => {
      setRespondingChoiceId(callId);
      try {
        return await respondToPendingChoice(callId, value, {
          send: (reply) => sendMessage('.', undefined, undefined, { toolChoice: reply }),
          clear: (id) => chat.setMessages((prev) => clearPendingChoice(prev, id)),
        });
      } finally {
        setRespondingChoiceId(null);
      }
    },
    [chat, sendMessage],
  );

  return { respondToConfirmation, respondingConfirmationId, respondToChoice, respondingChoiceId };
}
